import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
    Ticket, Lock, Unlock, CheckCircle2, Loader2, X, ShieldAlert,
    Sparkles, Trophy, ChevronRight, History, Phone, Calendar,
    Target, TrendingUp, Clock, XCircle,
} from "lucide-react";
import { toast } from "sonner";


// ---------- Utility ----------
const fmtKickoff = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString("sw-TZ", {
            day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
        });
    } catch (_) {
        return iso;
    }
};

const CONFIDENCE_TONE = (c) => {
    if (c >= 75) return "text-emerald-400 border-emerald-400/40 bg-emerald-400/10";
    if (c >= 55) return "text-yellow-300 border-yellow-300/40 bg-yellow-300/10";
    return "text-orange-300 border-orange-300/40 bg-orange-300/10";
};


const ResultBadge = ({ status, actual }) => {
    if (status === "won") {
        return (
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/40 border text-[10px]" data-testid="result-badge-won">
                <CheckCircle2 className="h-3 w-3 mr-1" /> IMESHINDA
                {actual && ` · ${actual.home_score}-${actual.away_score}`}
            </Badge>
        );
    }
    if (status === "lost") {
        return (
            <Badge className="bg-red-500/20 text-red-400 border-red-500/40 border text-[10px]" data-testid="result-badge-lost">
                <XCircle className="h-3 w-3 mr-1" /> IMESHINDWA
                {actual && ` · ${actual.home_score}-${actual.away_score}`}
            </Badge>
        );
    }
    if (status === "void") {
        return (
            <Badge className="bg-white/10 text-white/60 border-white/20 border text-[10px]" data-testid="result-badge-void">
                Imefutwa
            </Badge>
        );
    }
    return (
        <Badge className="bg-white/[0.04] text-white/40 border-white/10 border text-[10px]" data-testid="result-badge-pending">
            <Clock className="h-3 w-3 mr-1" /> Inasubiri
        </Badge>
    );
};


const AccuracyTile = ({ stats }) => {
    if (!stats || stats.total_slips === 0) return null;
    const acc = stats.lifetime_accuracy_pct;
    const tone = acc == null ? "text-white/60"
        : acc >= 60 ? "text-emerald-400"
        : acc >= 40 ? "text-yellow-300"
        : "text-orange-300";
    return (
        <Card className="bg-gradient-to-br from-electric/10 to-paper border-electric/30 p-4 mb-6" data-testid="slip-accuracy-tile">
            <div className="flex items-center gap-2 mb-2">
                <Target className="h-4 w-4 text-electric" />
                <span className="text-[10px] uppercase tracking-[0.18em] text-electric font-heading">Utendaji wako</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                    <div className="text-[10px] text-white/50 uppercase tracking-wider mb-0.5">Usahihi</div>
                    <div className={`font-heading font-black text-3xl tabular-nums ${tone}`} data-testid="stat-accuracy">
                        {acc == null ? "—" : `${acc}%`}
                    </div>
                </div>
                <div>
                    <div className="text-[10px] text-white/50 uppercase tracking-wider mb-0.5">Sahihi</div>
                    <div className="font-heading font-black text-3xl tabular-nums text-emerald-400" data-testid="stat-wins">{stats.wins}</div>
                </div>
                <div>
                    <div className="text-[10px] text-white/50 uppercase tracking-wider mb-0.5">Zisizosahihi</div>
                    <div className="font-heading font-black text-3xl tabular-nums text-red-400" data-testid="stat-losses">{stats.losses}</div>
                </div>
                <div>
                    <div className="text-[10px] text-white/50 uppercase tracking-wider mb-0.5">Zinasubiri</div>
                    <div className="font-heading font-black text-3xl tabular-nums text-white/70" data-testid="stat-pending">{stats.pending}</div>
                </div>
            </div>
            {stats.fully_settled_slips > 0 && (
                <div className="mt-2 text-[11px] text-white/50">
                    Mkeka {stats.fully_settled_slips} umekamilika · wastani wa mkeka: {stats.avg_slip_accuracy_pct ?? "—"}%
                </div>
            )}
        </Card>
    );
};


// ---------- Sub-components ----------
const MatchTile = ({ m, selected, disabled, onToggle }) => (
    <button
        type="button"
        onClick={() => onToggle(m)}
        disabled={disabled}
        className={`text-left p-3 rounded-md border transition-all w-full ${
            selected
                ? "border-electric bg-electric/10 shadow-[0_0_0_1px_rgba(59,130,246,0.4)]"
                : "border-border bg-paper hover:border-electric/40"
        } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
        data-testid={`slip-picker-match-${m.match_id}`}
    >
        <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] text-white/40 uppercase tracking-wider truncate">
                {m.competition || "Mechi"}
            </span>
            {selected && (
                <CheckCircle2 className="h-4 w-4 text-electric ml-auto flex-shrink-0" />
            )}
        </div>
        <div className="flex items-center gap-2">
            {m.home_logo && (
                <img
                    src={m.home_logo}
                    alt=""
                    className="w-5 h-5 object-contain flex-shrink-0"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
            )}
            <span className="text-white text-sm font-medium truncate flex-1">{m.home_team}</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
            {m.away_logo && (
                <img
                    src={m.away_logo}
                    alt=""
                    className="w-5 h-5 object-contain flex-shrink-0"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
            )}
            <span className="text-white/85 text-sm font-medium truncate flex-1">{m.away_team}</span>
        </div>
        {m.kickoff && (
            <div className="flex items-center gap-1 mt-1.5 text-[11px] text-white/50">
                <Calendar className="h-3 w-3" />
                {fmtKickoff(m.kickoff)}
            </div>
        )}
    </button>
);


const SlipSummary = ({
    selected, onRemove, onClear, phone, setPhone, price, onPay, submitting, canSubmit,
}) => (
    <Card className="bg-paper border-electric/40 p-5 sticky top-24" data-testid="slip-summary">
        <div className="flex items-center gap-2 mb-3">
            <Ticket className="h-4 w-4 text-electric" />
            <h3 className="font-heading text-lg font-black text-white uppercase tracking-wider">
                Utabiri Wangu
            </h3>
            <Badge className="ml-auto bg-electric/20 text-electric border-electric/40 border font-mono">
                {selected.length}/10
            </Badge>
        </div>

        {/* Phone input — ALWAYS visible so returning customers can pull history + stats
            even before picking matches. */}
        <label className="block mb-3">
            <span className="text-[11px] text-white/50 uppercase tracking-wider flex items-center gap-1.5 mb-1">
                <Phone className="h-3 w-3" /> Namba yako ya simu
            </span>
            <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+2557XXXXXXXX"
                className="bg-obsidian border-border text-white"
                data-testid="slip-phone-input"
            />
        </label>

        {selected.length === 0 ? (
            <div className="py-6 text-center text-white/50 text-xs" data-testid="slip-empty">
                Chagua mechi upande wa kushoto ili kuanza mkeka wako wa utabiri.
            </div>
        ) : (
            <>
                <ul className="space-y-1.5 mb-4 max-h-64 overflow-y-auto pr-1" data-testid="slip-picks">
                    {selected.map((m) => (
                        <li
                            key={m.match_id}
                            className="flex items-center gap-2 p-2 rounded bg-obsidian border border-border"
                            data-testid={`slip-selected-${m.match_id}`}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="text-white text-xs font-medium truncate">
                                    {m.home_team} <span className="text-white/40">vs</span> {m.away_team}
                                </div>
                                <div className="text-[10px] text-white/40 truncate">{m.competition}</div>
                            </div>
                            <button
                                onClick={() => onRemove(m.match_id)}
                                className="text-white/30 hover:text-red-400"
                                data-testid={`slip-remove-${m.match_id}`}
                                aria-label="Ondoa"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </li>
                    ))}
                </ul>

                <div className="border-t border-border pt-3 space-y-3">
                    <div className="flex items-baseline justify-between p-3 rounded bg-obsidian border border-border">
                        <span className="text-white/60 text-xs uppercase tracking-wider">Bei</span>
                        <span className="font-heading font-black text-electric text-xl tabular-nums">
                            TZS {price.toLocaleString()}
                        </span>
                    </div>

                    <Button
                        onClick={onPay}
                        disabled={!canSubmit || submitting}
                        className="w-full bg-electric hover:bg-electric/80 text-white font-heading font-black uppercase tracking-wider"
                        data-testid="slip-pay-btn"
                    >
                        {submitting ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Inatuma...</>
                        ) : (
                            <><Lock className="h-4 w-4 mr-2" /> Lipia & Fungua Utabiri</>
                        )}
                    </Button>

                    <button
                        onClick={onClear}
                        className="w-full text-[11px] text-white/40 hover:text-white/80"
                        data-testid="slip-clear-btn"
                    >
                        Futa mkeka
                    </button>
                </div>
            </>
        )}
    </Card>
);


const RevealedSlip = ({ slip, onNewSlip }) => {
    const preds = slip.predictions || [];
    const matchById = useMemo(() => {
        const map = {};
        (slip.matches || []).forEach((m) => { map[m.match_id] = m; });
        return map;
    }, [slip.matches]);

    const wins = slip.settled_wins || preds.filter((p) => p.result_status === "won").length;
    const losses = slip.settled_losses || preds.filter((p) => p.result_status === "lost").length;
    const settled = wins + losses;
    const pending = preds.filter((p) => !p.result_status || p.result_status === "pending").length;
    const slipAcc = slip.slip_accuracy_pct
        ?? (settled > 0 ? Math.round((wins / settled) * 100) : null);
    const isScorecard = settled > 0 || pending < preds.length;

    return (
        <div className="space-y-5" data-testid="slip-revealed">
            <Card className="bg-gradient-to-r from-emerald-500/10 to-paper border-emerald-500/40 p-5">
                <div className="flex items-center gap-3 flex-wrap">
                    <Unlock className="h-6 w-6 text-emerald-400" />
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-400 font-heading">
                            Malipo yamefanikiwa
                        </div>
                        <div className="text-white text-xl font-heading font-bold mt-0.5">
                            Utabiri wako {preds.length}/{(slip.matches || []).length} umefunguliwa
                        </div>
                    </div>
                    {slip.combined_confidence != null && (
                        <div className="ml-auto text-right" data-testid="slip-combined">
                            <div className="text-[10px] text-white/50 uppercase tracking-wider">Uwezekano wa jumla</div>
                            <div className="font-heading font-black text-emerald-400 text-3xl tabular-nums">
                                {slip.combined_confidence}%
                            </div>
                        </div>
                    )}
                </div>
            </Card>

            {/* Post-match scorecard header — only when at least one match has settled */}
            {isScorecard && (
                <Card className="bg-paper border-electric/40 p-4" data-testid="slip-scorecard">
                    <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="h-4 w-4 text-electric" />
                        <span className="text-[10px] uppercase tracking-[0.18em] text-electric font-heading">Alama ya mkeka</span>
                        {slip.fully_settled_at && (
                            <Badge className="ml-auto bg-emerald-500/20 text-emerald-400 border-emerald-500/40 border text-[10px]">
                                Kamilika
                            </Badge>
                        )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div>
                            <div className="text-[10px] text-white/50 uppercase tracking-wider mb-0.5">Usahihi</div>
                            <div className="font-heading font-black text-2xl tabular-nums text-electric" data-testid="scorecard-accuracy">
                                {slipAcc == null ? "—" : `${slipAcc}%`}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] text-white/50 uppercase tracking-wider mb-0.5">Sahihi</div>
                            <div className="font-heading font-black text-2xl tabular-nums text-emerald-400">{wins}</div>
                        </div>
                        <div>
                            <div className="text-[10px] text-white/50 uppercase tracking-wider mb-0.5">Zisizosahihi</div>
                            <div className="font-heading font-black text-2xl tabular-nums text-red-400">{losses}</div>
                        </div>
                        <div>
                            <div className="text-[10px] text-white/50 uppercase tracking-wider mb-0.5">Zinasubiri</div>
                            <div className="font-heading font-black text-2xl tabular-nums text-white/70">{pending}</div>
                        </div>
                    </div>
                </Card>
            )}

            <div className="grid gap-3" data-testid="slip-predictions-list">
                {preds.map((p, i) => {
                    const m = matchById[p.match_id] || {};
                    return (
                        <Card
                            key={p.match_id}
                            className="bg-paper border-border p-4"
                            data-testid={`slip-pred-${p.match_id}`}
                        >
                            <div className="flex items-start gap-3 flex-wrap">
                                <div className="flex-1 min-w-[240px]">
                                    <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">
                                        Mechi {i + 1} · {m.competition || ""}
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap mb-2">
                                        {m.home_logo && <img src={m.home_logo} alt="" className="w-5 h-5 object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                                        <span className="text-white font-bold">{m.home_team}</span>
                                        <span className="text-white/40 text-xs">vs</span>
                                        {m.away_logo && <img src={m.away_logo} alt="" className="w-5 h-5 object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                                        <span className="text-white font-bold">{m.away_team}</span>
                                    </div>
                                    {m.kickoff && (
                                        <div className="text-[11px] text-white/50 flex items-center gap-1">
                                            <Calendar className="h-3 w-3" /> {fmtKickoff(m.kickoff)}
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                                    <ResultBadge status={p.result_status} actual={p.actual_result} />
                                    <Badge className={`text-xs font-bold border ${CONFIDENCE_TONE(p.confidence)}`}>
                                        <Trophy className="h-3 w-3 mr-1" />
                                        {p.winner_label_sw}
                                    </Badge>
                                    <div className="font-heading font-black text-xl tabular-nums text-white">
                                        {p.confidence}<span className="text-sm text-white/50">%</span>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-3 pt-3 border-t border-border">
                                <p className="text-sm text-white/80 leading-relaxed" data-testid={`slip-reasoning-${p.match_id}`}>
                                    {p.reasoning_sw}
                                </p>

                                {p.key_factors && p.key_factors.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {p.key_factors.map((k, idx) => (
                                            <span key={idx} className="text-[11px] px-2 py-0.5 rounded-full bg-obsidian border border-border text-white/60">
                                                {k}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </Card>
                    );
                })}
            </div>

            <Card className="bg-orange-400/[0.06] border-orange-400/40 p-4" data-testid="slip-disclaimer">
                <div className="flex gap-3">
                    <ShieldAlert className="h-5 w-5 text-orange-300 flex-shrink-0 mt-0.5" />
                    <p className="text-orange-200/90 text-xs leading-relaxed">
                        {slip.disclaimer_sw}
                    </p>
                </div>
            </Card>

            <Button
                onClick={onNewSlip}
                variant="outline"
                className="border-electric/40 text-electric hover:bg-electric/10"
                data-testid="slip-new-btn"
            >
                Anza mkeka mpya <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
        </div>
    );
};


// ---------- Main page ----------
const UtabiriWanguPage = () => {
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState([]);
    const [phone, setPhone] = useState(() => {
        try { return localStorage.getItem("dimbani_slip_phone") || ""; } catch (_) { return ""; }
    });
    const [submitting, setSubmitting] = useState(false);
    const [paidSlip, setPaidSlip] = useState(null);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [stats, setStats] = useState(null);

    // Load upcoming matches
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const r = await axios.get(`${API}/slips/upcoming-matches?limit=30`, { timeout: 20000 });
                if (!cancelled) setMatches(r.data.matches || []);
            } catch (_e) {
                if (!cancelled) toast.error("Imeshindwa kupakia ratiba");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Persist phone
    useEffect(() => {
        try {
            if (phone) localStorage.setItem("dimbani_slip_phone", phone);
        } catch (_) { /* noop */ }
    }, [phone]);

    // Load history for a phone
    const loadHistory = useCallback(async (p) => {
        if (!p || p.length < 6) { setHistory([]); setStats(null); return; }
        setHistoryLoading(true);
        try {
            const [historyRes, statsRes] = await Promise.all([
                axios.get(`${API}/slips/mine?phone=${encodeURIComponent(p)}&limit=8`, { timeout: 15000 }),
                axios.get(`${API}/slips/stats?phone=${encodeURIComponent(p)}`, { timeout: 15000 }),
            ]);
            setHistory(historyRes.data.slips || []);
            setStats(statsRes.data || null);
        } catch (_e) {
            setHistory([]);
            setStats(null);
        } finally {
            setHistoryLoading(false);
        }
    }, []);

    useEffect(() => {
        const t = setTimeout(() => loadHistory(phone), 400);
        return () => clearTimeout(t);
    }, [phone, loadHistory]);

    const toggle = (m) => {
        setSelected((prev) => {
            if (prev.some((x) => x.match_id === m.match_id)) {
                return prev.filter((x) => x.match_id !== m.match_id);
            }
            if (prev.length >= 10) {
                toast.warning("Umefikia kikomo cha mechi 10");
                return prev;
            }
            return [...prev, m];
        });
    };

    const remove = (id) => setSelected((prev) => prev.filter((x) => x.match_id !== id));
    const clear = () => setSelected([]);

    const canSubmit = selected.length >= 1 && phone.trim().length >= 6;
    const price = 1000; // matches PRICE_PER_SLIP on the backend

    const pay = async () => {
        setSubmitting(true);
        try {
            // 1. Create the slip (draft)
            const createRes = await axios.post(`${API}/slips`, {
                phone_number: phone.trim(),
                matches: selected.map((m) => ({
                    match_id: m.match_id,
                    home_team: m.home_team,
                    away_team: m.away_team,
                    competition: m.competition,
                    kickoff: m.kickoff,
                    home_logo: m.home_logo,
                    away_logo: m.away_logo,
                })),
            }, { timeout: 25000 });

            const slip = createRes.data;

            // If they already have an active subscription, the slip is auto-paid.
            if (slip.unlocked) {
                setPaidSlip(slip);
                setSelected([]);
                toast.success("Umefunguliwa moja kwa moja kupitia mchango wako!");
                loadHistory(phone);
                window.scrollTo({ top: 0, behavior: "smooth" });
                return;
            }

            // 2. Pay (mock)
            const payRes = await axios.post(`${API}/slips/${slip.id}/pay`, {
                phone_number: phone.trim(),
                payment_method: "mock",
            }, { timeout: 60000 });

            setPaidSlip(payRes.data);
            setSelected([]);
            toast.success("Malipo yamefanikiwa! Utabiri umefunguliwa.");
            loadHistory(phone);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Malipo yameshindikana. Jaribu tena.");
        } finally {
            setSubmitting(false);
        }
    };

    const openHistorySlip = async (slipId) => {
        try {
            const r = await axios.get(`${API}/slips/${slipId}?phone=${encodeURIComponent(phone)}`, { timeout: 15000 });
            setPaidSlip(r.data);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (_e) {
            toast.error("Imeshindikana kupakia slip");
        }
    };

    if (paidSlip) {
        return (
            <div className="min-h-screen bg-background text-white">
                <header className="border-b border-border bg-paper/40 backdrop-blur sticky top-0 z-20">
                    <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
                        <Link to="/" className="dimbani-wordmark text-2xl">DIMBANI</Link>
                        <Link to="/utabiri-wangu" onClick={() => setPaidSlip(null)} className="text-electric text-sm hover:underline" data-testid="header-new-slip-link">
                            Anza mkeka mpya
                        </Link>
                    </div>
                </header>
                <main className="max-w-5xl mx-auto px-4 py-8">
                    <RevealedSlip slip={paidSlip} onNewSlip={() => setPaidSlip(null)} />
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background text-white" data-testid="utabiri-wangu-page">
            <header className="border-b border-border bg-paper/40 backdrop-blur sticky top-0 z-20">
                <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
                    <Link to="/" className="dimbani-wordmark text-2xl">DIMBANI</Link>
                    <nav className="hidden md:flex items-center gap-5 text-sm">
                        <Link to="/matokeo" className="text-white/60 hover:text-white">Matokeo</Link>
                        <Link to="/utabiri" className="text-white/60 hover:text-white">Utabiri</Link>
                        <Link to="/utabiri-wangu" className="text-electric font-bold">Utabiri Wangu</Link>
                    </nav>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 py-8">
                {/* Hero */}
                <div className="mb-8" data-testid="slip-hero">
                    <h1 className="font-heading text-4xl sm:text-5xl font-black text-white mb-2">
                        Utabiri Wangu
                    </h1>
                    <p className="text-white/70 text-base max-w-2xl">
                        Chagua mechi zinazokuja, lipia bei ndogo, na fungua utabiri kamili — timu itakayoshinda, uwezekano na uchambuzi. Tumia taarifa hizi kuboresha maamuzi yako.
                    </p>
                </div>

                {/* Disclaimer teaser (compact) */}
                <Card className="bg-orange-400/[0.05] border-orange-400/30 p-3 mb-6 flex items-center gap-3" data-testid="slip-disclaimer-teaser">
                    <ShieldAlert className="h-4 w-4 text-orange-300 flex-shrink-0" />
                    <p className="text-orange-200/80 text-[12px] leading-relaxed">
                        Utabiri huu unatokana na taarifa za umma (historia, takwimu, majeruhi, msimamo n.k.) na SI matokeo halisi ya mechi.
                    </p>
                </Card>

                {/* Lifetime accuracy scorecard — appears once phone has at least one slip */}
                <AccuracyTile stats={stats} />

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    {/* Picker */}
                    <div className="lg:col-span-2">
                        <div className="flex items-center gap-2 mb-3">
                            <Calendar className="h-4 w-4 text-white/60" />
                            <h2 className="font-heading text-lg font-bold text-white uppercase tracking-wider">
                                Mechi zinazokuja
                            </h2>
                            {matches.length > 0 && (
                                <Badge variant="outline" className="text-[10px] border-white/20 text-white/60 ml-auto">
                                    {matches.length} mechi
                                </Badge>
                            )}
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-24 text-white/40" data-testid="slip-picker-loading">
                                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Inapakia ratiba...
                            </div>
                        ) : matches.length === 0 ? (
                            <Card className="bg-paper border-border p-8 text-center text-white/50 text-sm" data-testid="slip-picker-empty">
                                Hakuna mechi zinazokuja kwa sasa. Rudi baadaye.
                            </Card>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="slip-picker-grid">
                                {matches.map((m) => (
                                    <MatchTile
                                        key={m.match_id}
                                        m={m}
                                        selected={selected.some((x) => x.match_id === m.match_id)}
                                        onToggle={toggle}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Slip summary sidebar */}
                    <div>
                        <SlipSummary
                            selected={selected}
                            onRemove={remove}
                            onClear={clear}
                            phone={phone}
                            setPhone={setPhone}
                            price={price}
                            onPay={pay}
                            submitting={submitting}
                            canSubmit={canSubmit}
                        />
                    </div>
                </div>

                {/* History */}
                {phone && phone.length >= 6 && (
                    <div className="mt-10" data-testid="slip-history-section">
                        <div className="flex items-center gap-2 mb-3">
                            <History className="h-4 w-4 text-white/60" />
                            <h2 className="font-heading text-lg font-bold text-white uppercase tracking-wider">
                                Mkeka wako uliopita
                            </h2>
                            {historyLoading && <Loader2 className="h-4 w-4 animate-spin text-white/40" />}
                        </div>
                        {history.length === 0 ? (
                            <Card className="bg-paper border-border p-6 text-center text-white/40 text-sm" data-testid="slip-history-empty">
                                Bado hujaunda mkeka wowote kwa namba hii.
                            </Card>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="slip-history-list">
                                {history.map((s) => {
                                    const sWins = s.settled_wins || 0;
                                    const sLosses = s.settled_losses || 0;
                                    const sSettled = sWins + sLosses;
                                    const sAcc = s.slip_accuracy_pct != null
                                        ? s.slip_accuracy_pct
                                        : (sSettled > 0 ? Math.round((sWins / sSettled) * 100) : null);
                                    return (
                                        <button
                                            key={s.id}
                                            onClick={() => openHistorySlip(s.id)}
                                            className="text-left p-3 rounded border border-border bg-paper hover:border-electric/40 transition-colors"
                                            data-testid={`slip-history-${s.id}`}
                                        >
                                            <div className="flex items-center gap-2 mb-1">
                                                <Badge className={s.status === "paid" ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40 border" : "bg-yellow-500/20 text-yellow-400 border-yellow-500/40 border"}>
                                                    {s.status === "paid" ? (
                                                        <><Unlock className="h-3 w-3 mr-1" /> Umefunguliwa</>
                                                    ) : (
                                                        <><Lock className="h-3 w-3 mr-1" /> Rasimu</>
                                                    )}
                                                </Badge>
                                                {sAcc != null ? (
                                                    <Badge className={`ml-1 border text-[10px] ${
                                                        sAcc >= 60 ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40"
                                                        : sAcc >= 40 ? "bg-yellow-500/15 text-yellow-300 border-yellow-500/40"
                                                        : "bg-red-500/15 text-red-400 border-red-500/40"
                                                    }`} data-testid={`slip-history-accuracy-${s.id}`}>
                                                        {sWins}/{sSettled} · {sAcc}%
                                                    </Badge>
                                                ) : (
                                                    <Badge className="ml-1 bg-white/[0.04] text-white/40 border-white/10 border text-[10px]">
                                                        <Clock className="h-3 w-3 mr-1" /> Inasubiri
                                                    </Badge>
                                                )}
                                                <span className="ml-auto text-[11px] text-white/40">
                                                    {s.created_at ? new Date(s.created_at).toLocaleString() : ""}
                                                </span>
                                            </div>
                                            <div className="text-white text-sm font-medium">
                                                {(s.matches || []).length} mechi · TZS {(s.price_amount || 0).toLocaleString()}
                                            </div>
                                            <div className="text-[11px] text-white/50 mt-0.5 truncate">
                                                {(s.matches || []).slice(0, 2).map((m) => `${m.home_team} v ${m.away_team}`).join(" · ")}
                                                {(s.matches || []).length > 2 ? "..." : ""}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Full disclaimer at the bottom */}
                <Card className="mt-10 bg-orange-400/[0.06] border-orange-400/40 p-4" data-testid="slip-disclaimer-full">
                    <div className="flex gap-3">
                        <ShieldAlert className="h-5 w-5 text-orange-300 flex-shrink-0 mt-0.5" />
                        <p className="text-orange-200/90 text-xs leading-relaxed">
                            <span className="font-bold text-orange-200">Angalizo:</span> Utabiri unaotolewa hapa umeandaliwa kwa kutumia taarifa za umma zinazopatikana kuhusu timu — historia, takwimu, majeruhi, msimamo, hali ya sasa, na mambo mengine yanayoweza kuathiri matokeo. Utabiri huu SI matokeo halisi na hautoi uhakikisho wa mafanikio. Tumia taarifa hizi kama mwongozo tu.
                        </p>
                    </div>
                </Card>
            </main>
        </div>
    );
};

export default UtabiriWanguPage;
